import React, { useEffect, useState, useRef } from "react";
import { api, imageSrc, CATEGORY_META } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Eye, EyeSlash, UploadSimple, X } from "@phosphor-icons/react";

const CATEGORIES = Object.keys(CATEGORY_META);
const emptyProduct = {
  name: "", description: "", tagline: "", category: CATEGORIES[0],
  country: "South Korea", image_url: "", is_published: true,
};

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/products", { params: { include_unpublished: true } }).then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ ...emptyProduct }); setShowForm(true); };
  const startEdit = (p) => { setEditing({ ...p }); setShowForm(true); };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  const togglePublish = async (p) => {
    await api.patch(`/admin/products/${p.id}/publish`, null, { params: { is_published: !p.is_published } });
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter">Products</h1>
          <p className="text-slate-600 mt-1">{products.length} items in catalog</p>
        </div>
        <button onClick={startNew} data-testid="admin-add-product" className="btn-pill-primary">
          <Plus size={16} weight="bold" /> Add product
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-products-grid">
        {products.map((p) => (
          <div key={p.id} className="brutal-card rounded-3xl overflow-hidden" data-testid={`admin-product-${p.id}`}>
            <div className="aspect-[5/4] bg-slate-100 relative overflow-hidden">
              {p.image_url && (
                <img src={imageSrc(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
              )}
              {!p.is_published && (
                <div className="absolute top-3 left-3 sticker bg-slate-900 text-white border-slate-900">Draft</div>
              )}
            </div>
            <div className="p-5">
              <div className="label-uppercase text-slate-500">{CATEGORY_META[p.category]?.name || p.category}</div>
              <h3 className="font-display text-lg font-black mt-1">{p.name}</h3>
              <p className="text-sm text-slate-600 line-clamp-2 mt-1">{p.description}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <button onClick={() => startEdit(p)} data-testid={`edit-product-${p.id}`} className="sticker">
                  <PencilSimple size={12} weight="bold" /> Edit
                </button>
                <button onClick={() => togglePublish(p)} data-testid={`publish-toggle-${p.id}`} className="sticker">
                  {p.is_published ? <><EyeSlash size={12} weight="bold" /> Unpublish</> : <><Eye size={12} weight="bold" /> Publish</>}
                </button>
                <button onClick={() => onDelete(p)} data-testid={`delete-product-${p.id}`} className="sticker text-rose-600 border-rose-300 hover:bg-rose-50">
                  <Trash size={12} weight="bold" /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <ProductForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function ProductForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const upload = async (file) => {
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const r = await api.post("/admin/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name, description: form.description, tagline: form.tagline || "",
        category: form.category, country: form.country, image_url: form.image_url,
        is_published: form.is_published,
      };
      if (form.id) await api.put(`/admin/products/${form.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6" data-testid="product-form-modal">
      <div className="bg-white w-full max-w-2xl rounded-t-3xl md:rounded-3xl border-2 border-[#0F172A] max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="font-display text-xl font-black">{form.id ? "Edit product" : "New product"}</h2>
          <button onClick={onClose} data-testid="form-close" className="w-10 h-10 rounded-full border-2 border-[#0F172A] flex items-center justify-center">
            <X size={18} weight="bold" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-6 grid md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="label-uppercase block mb-2">Image</label>
            <div className="flex items-center gap-4">
              <div className="w-28 h-28 rounded-2xl border-2 border-dashed border-slate-300 overflow-hidden flex items-center justify-center bg-slate-50">
                {form.image_url ? <img src={imageSrc(form.image_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-3xl">🖼️</span>}
              </div>
              <div className="flex-1">
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="form-image-file" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="sticker">
                  <UploadSimple size={12} weight="bold" /> {uploading ? "Uploading…" : "Upload image"}
                </button>
                <div className="mt-3">
                  <input
                    type="text"
                    placeholder="or paste image URL"
                    value={form.image_url || ""}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    data-testid="form-image-url"
                    className="w-full text-xs bg-white border-2 border-slate-200 rounded-xl px-3 py-2"
                  />
                </div>
              </div>
            </div>
          </div>
          <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="form-name" />
          <Field label="Tagline" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testid="form-tagline" />
          <div className="md:col-span-2">
            <label className="label-uppercase block mb-2">Description *</label>
            <textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description"
              className="w-full bg-white border-2 border-slate-200 rounded-2xl px-4 py-3 focus:border-[#FF6B9E] focus:outline-none font-medium resize-none" />
          </div>
          <div>
            <label className="label-uppercase block mb-2">Category *</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="form-category"
              className="w-full bg-white border-2 border-slate-200 rounded-2xl px-4 py-3 focus:border-[#FF6B9E] focus:outline-none font-medium">
              {CATEGORIES.map((c) => <option key={c} value={c}>{CATEGORY_META[c].name}</option>)}
            </select>
          </div>
          <Field label="Country of manufacture" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="form-country" />
          <div className="md:col-span-2 flex items-center gap-3">
            <input type="checkbox" id="pub" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} data-testid="form-published" />
            <label htmlFor="pub" className="text-sm font-semibold">Publish immediately</label>
          </div>
          <div className="md:col-span-2 flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="btn-pill-ghost">Cancel</button>
            <button type="submit" disabled={saving} data-testid="form-save" className="btn-pill-primary disabled:opacity-60">
              {saving ? "Saving…" : "Save product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, value, onChange, testid }) {
  return (
    <div>
      <label className="label-uppercase block mb-2">{label}{required && " *"}</label>
      <input type="text" required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border-2 border-slate-200 rounded-2xl px-4 py-3 focus:border-[#FF6B9E] focus:outline-none font-medium" />
    </div>
  );
}
